import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import type { IconDef } from '../icons/icon';
import { IconComponent } from '../icons/icon-reusable';

/**
 * Selectable option card behaving as one radio in a group.
 * - `row`: icon tile, title, description and optional feature bullets, radio on the right.
 * - `tile`: compact card, small icon top-left, check badge top-right, title + description below.
 * - `inline`: single row — small icon, title, check badge on the right.
 * Wrap a set of them in an element with `role="radiogroup"`.
 */
@Component({
  selector: 'app-choice-card-reusable',
  standalone: true,
  imports: [CommonModule, IconComponent],
  host: { '[attr.title]': 'null', class: 'block' },
  template: `
    <button
      type="button"
      role="radio"
      [attr.aria-checked]="selected"
      [disabled]="disabled"
      class="flex h-full w-full cursor-pointer rounded-xl border text-left transition focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo300 disabled:cursor-not-allowed disabled:opacity-60"
      [ngClass]="[
        selected ? 'border-indigo500 bg-indigo50' : 'border-slate300 bg-white hover:border-indigo300',
        layout === 'row' ? 'items-center gap-4 p-4' : layout === 'inline' ? 'items-center gap-3 px-3 py-2.5' : 'flex-col gap-3 p-3'
      ]"
      (click)="select.emit()"
    >
      @if (layout === 'inline') {
        @if (icon) {
          <span class="flex h-8 w-8 shrink-0 items-center justify-center rounded-md" [ngClass]="selected ? 'bg-indigo100 text-indigo600' : 'bg-slate100 text-slate700'">
            <app-icon-reusable [icon]="icon" [size]="16" [useCurrentColor]="true"></app-icon-reusable>
          </span>
        }
        <span class="min-w-0 flex-1 truncate text-sm font-medium text-slate900">{{ title }}</span>
        <ng-container [ngTemplateOutlet]="checkMark"></ng-container>
      } @else if (layout === 'row') {
        @if (icon) {
          <span class="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg" [ngClass]="selected ? 'bg-indigo100' : 'bg-slate100'">
            <app-icon-reusable [icon]="icon" [size]="22"></app-icon-reusable>
          </span>
        }
        <span class="min-w-0 flex-1">
          <span class="block text-base font-semibold text-slate900">{{ title }}</span>
          @if (description) {
            <span class="mt-0.5 block text-sm text-slate700">{{ description }}</span>
          }
          @if (features.length) {
            <span class="mt-1 flex flex-wrap gap-x-4 gap-y-1">
              @for (feature of features; track feature) {
                <span class="inline-flex items-center gap-1.5 text-xs text-slate600">
                  <span class="h-1 w-1 rounded-full bg-indigo600"></span>{{ feature }}
                </span>
              }
            </span>
          }
        </span>
        <span
          class="flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2"
          [ngClass]="selected ? 'border-indigo600' : 'border-slate500'"
        >
          @if (selected) {
            <span class="h-2.5 w-2.5 rounded-full bg-indigo600"></span>
          }
        </span>
      } @else {
        <span class="flex w-full items-start justify-between">
          @if (icon) {
            <span class="flex h-8 w-8 items-center justify-center rounded-md" [ngClass]="selected ? 'bg-indigo100 text-indigo600' : 'bg-slate100 text-slate700'">
              <app-icon-reusable [icon]="icon" [size]="16" [useCurrentColor]="true"></app-icon-reusable>
            </span>
          }
          <ng-container [ngTemplateOutlet]="checkMark"></ng-container>
        </span>
        <span>
          <span class="block text-sm font-medium text-slate900">{{ title }}</span>
          @if (description) {
            <span class="mt-0.5 block text-xs text-slate600">{{ description }}</span>
          }
        </span>
      }
    </button>

    <ng-template #checkMark>
      @if (selected) {
        <span class="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-indigo600 text-white">
          <svg width="10" height="10" viewBox="0 0 12 12" fill="none" aria-hidden="true">
            <path d="M2.5 6.2L4.8 8.5L9.5 3.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </span>
      } @else {
        <span class="h-5 w-5 shrink-0 rounded-full border border-slate300 bg-white"></span>
      }
    </ng-template>
  `,
})
export class ChoiceCardReusableComponent {
  @Input() title = '';
  @Input() description = '';
  @Input() features: readonly string[] = [];
  @Input() icon: IconDef | null = null;
  @Input() selected = false;
  @Input() disabled = false;
  @Input() layout: 'row' | 'tile' | 'inline' = 'row';

  @Output() select = new EventEmitter<void>();
}
